import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';

/**
 * Sticky-header pill that surfaces draft persistence state + an explicit "Save draft"
 * button. Used by both the standalone PublicFormPage and the form-flow shell so the
 * respondent gets the same "my work is safe" signal on either code path.
 *
 * Priority for the status label:
 *   1. 'saving' → "Saving..."
 *   2. 'restored' → "Draft restored" (green, transient)
 *   3. otherwise, if `lastSavedAt` is set → relative-time label ("Saved 2m ago")
 *
 * The relative-time label refreshes every 30 s so the minute count stays honest
 * without another save firing.
 */
export default function FormDraftStatusBar({
  palette,
  autoSaveStatus,
  lastSavedAt,
  savingDraft = false,
  disabled = false,
  onSaveDraft = null,
}) {
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!lastSavedAt) return undefined;
    const id = setInterval(() => forceTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [lastSavedAt]);

  const savedLabel = useMemo(() => {
    if (!lastSavedAt) return '';
    const diffSec = Math.max(0, Math.floor((Date.now() - lastSavedAt) / 1000));
    if (diffSec < 10) return 'Saved just now';
    if (diffSec < 60) return `Saved ${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `Saved ${diffMin}m ago`;
    const d = new Date(lastSavedAt);
    return `Saved at ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }, [lastSavedAt]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {autoSaveStatus === 'saving' ? (
        <Typography variant="caption" sx={{ color: palette.textSecondary, fontSize: '11px', opacity: 0.7 }}>
          Saving...
        </Typography>
      ) : autoSaveStatus === 'restored' ? (
        <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '11px' }}>
          Draft restored
        </Typography>
      ) : savedLabel ? (
        <Typography variant="caption" sx={{ color: palette.textSecondary, fontSize: '11px', opacity: 0.7 }}>
          {savedLabel}
        </Typography>
      ) : null}

      {onSaveDraft && (
        <Button
          size="small"
          variant="outlined"
          onClick={onSaveDraft}
          disabled={disabled || savingDraft}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 500,
            borderColor: palette.border,
            color: palette.textPrimary,
            borderRadius: '8px',
            px: 2,
            '&:hover': {
              borderColor: palette.textSecondary,
              bgcolor: palette.chipBg,
            },
          }}
        >
          {savingDraft ? 'Saving...' : 'Save draft'}
        </Button>
      )}
    </Box>
  );
}
